/**
 * 后端的「错误信封」——<b>全仓唯一一份定义</b>。
 *
 * <p>约定是这样的：后端把业务失败表达成一个 body，而不是（或者说不总是）一个 HTTP 状态码：
 * <pre>
 * { errorCode: 400, errorMessage: '…', subtype: 'shopify_scope_required', data: { … } }
 * </pre>
 * 判定它<b>只看 <code>errorCode</code> 与 <code>errorMessage</code> 两个键在不在</b>，不看值、不看状态码。
 *
 * <p><b>为什么要有这个文件。</b>在这之前，同一个谓词在仓库里有<b>七份</b>：
 * <code>shopify/scopeError.ts</code> 里一份，另外六份分别抄在
 * <code>operationReportApi</code> / <code>operationSettingsApi</code> / <code>operationAlertsApi</code> /
 * <code>weeklyOperationReportApi</code> / <code>operationSalesApi</code> / <code>sourcingSignalsApi</code> 里，
 * 判定式一字不差，只是<b>类型谓词各写宽窄不一</b>（有的声明了 <code>subtype</code>、有的没有、
 * 有的带 <code>data</code>）。<code>api.ts</code> 的拦截器里还另有三处手写的同款形状检查，
 * 其中一处查的是 <code>errorCode + subtype</code> 而不是 <code>errorCode + errorMessage</code> ——
 * <b>七份抄写已经开始分叉了</b>。信封的形状是<b>前后端之间的契约</b>，契约不该有七份副本。
 *
 * <p>放在 <code>lib/core</code> 而不是 <code>services/shopify</code>：它跟 Shopify 无关，
 * 是每一个后端调用都会遇到的东西。<code>scopeError.ts</code> 现在从这里再导出，
 * 老的 import 路径继续可用（那边还有 <code>ShopifyScopeError</code> 等 Shopify 专属的东西）。
 */

/** 后端错误信封，也就是拦截器交回调用方的那个形状。 */
export interface ErrorEnvelope {
  errorCode: number;
  errorMessage: string;
  /** 机器可判的失败种类。<b>判定分支一律用它</b>，不要拿 errorMessage 做子串匹配。 */
  subtype?: string;
  /** 该 subtype 自己的负载，形状由 subtype 定。 */
  data?: unknown;
}

/**
 * 这是一个错误信封吗。
 *
 * <p><b>只看两个键在不在</b>，与合并前那七份完全一致 —— 这次收敛不改变任何一处的判定结果。
 */
export function isErrorEnvelope(data: unknown): data is ErrorEnvelope {
  return !!data && typeof data === 'object' && 'errorCode' in data && 'errorMessage' in data;
}

/**
 * 拿一句可以给商家看的话，拿不到就用调用方给的兜底。
 *
 * <p>合并前，三十来处调用点各自写着 <code>data.errorMessage || '…'</code>，兜底句子人人不同。
 * 抽出来不是为了省字，是为了让「后端没给话时说什么」变成调用点<b>显式传进来的一个参数</b>，
 * 而不是一个容易漏掉的 <code>||</code>。
 */
export function envelopeMessage(envelope: ErrorEnvelope, fallback: string): string {
  const message = envelope.errorMessage?.trim();
  return message || fallback;
}
